<template>
  <div class="kanban-hierarchy-wrapper">

    <div v-if="activity.level && activity.level > 0" class="hierarchy-indicator-kanban"
      :style="{ marginLeft: `${activity.level * 12}px` }">
      <v-icon size="10" color="grey-darken-1">mdi-subdirectory-arrow-right</v-icon>
    </div>

    <v-card class="kanban-card mb-3 d-flex flex-column" flat border @click="handleCardClick" min-height="250px">
      <v-card-title class="d-flex align-center justify-space-between py-3 px-4 pb-2" style="gap: 8px;">

        <div class="d-flex align-center flex-grow-1 min-width-0" style="gap: 8px;">

          <!-- <v-btn v-if="hasChildren" icon size="x-small" variant="text" @click.stop="toggleChildren" class="mr-1">
            <v-icon size="12" :class="{ 'rotate-90': showChildren }">
              mdi-chevron-right
            </v-icon>
          </v-btn> -->

          <h4 class="text-subtitle-1 font-weight-medium"
            style="min-width: 0; overflow-wrap: anywhere; word-break: break-word; white-space: normal;">
            {{ activity.order }}. {{ activity.title }}
          </h4>
        </div>

        <div class="mx-2 d-flex align-center flex-shrink-0" style="width: fit-content;">
          <v-select :model-value="activity.status" :items="statusOptions" item-title="title" item-value="value"
            density="compact" variant="flat" hide-details :color="getStatusColor(activity.status)"
            @update:model-value="handleStatusChange" @click.stop style="min-width: 120px; width: fit-content;">
            <template #selection="{ item }">
              <v-chip :color="getStatusColor(item.value)" size="small" variant="flat" class="px-2"
                style="pointer-events: none;">
                {{ item.title }}
              </v-chip>
            </template>

            <template #item="{ props, item }">
              <v-list-item v-bind="props">
                <template #prepend>
                  <v-icon :color="getStatusColor(item.value)" size="16">
                    {{ getStatusIcon(item.value) }}
                  </v-icon>
                </template>
              </v-list-item>
            </template>
          </v-select>
        </div>

        <div class="d-flex align-center gap-1 flex-shrink-0">
          <v-icon v-if="activity.hasAlert" color="warning" size="16">
            mdi-alert-triangle
          </v-icon>

          <v-menu v-if="authStore.isAdmin() || canEditOrAddSubtask">
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="x-small" />
            </template>
            <v-list density="compact">
              <v-list-item @click="handleView">
                <template #prepend>
                  <v-icon>mdi-eye-outline</v-icon>
                </template>
                <v-list-item-title>Visualizar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="authStore.isAdmin() || canEditActivity" @click="handleEdit">
                <template #prepend>
                  <v-icon>mdi-pencil</v-icon>
                </template>
                <v-list-item-title>Editar</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="authStore.isAdmin() || canAddSubtask" @click="handleAddSubtask(activity.id)">
                <template #prepend>
                  <v-icon>mdi-plus</v-icon>
                </template>
                <v-list-item-title>{{ getAddSubtaskMessage(activity) }}</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="authStore.isAdmin()" @click="handleViewHistory">
                <template #prepend>
                  <v-icon>mdi-history</v-icon>
                </template>
                <v-list-item-title>Histórico</v-list-item-title>
              </v-list-item>
              <v-list-item v-if="authStore.isAdmin()" @click="handleDelete">
                <template #prepend>
                  <v-icon>mdi-delete</v-icon>
                </template>
                <v-list-item-title>Excluir</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-menu v-else>
            <template #activator="{ props }">
              <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="x-small" />
            </template>
            <v-list density="compact">
              <v-list-item @click="handleView">
                <template #prepend>
                  <v-icon>mdi-eye-outline</v-icon>
                </template>
                <v-list-item-title>Visualizar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

        </div>
      </v-card-title>

      <v-card-text class="py-3 px-4 pt-0 flex-grow-1 d-flex flex-column justify-space-between">
        <p v-if="activity.description" class="text-body-2 text-medium-emphasis mb-3 description-text">
          {{ activity.description }}
        </p>

        <div class="mb-3">
          <div class="d-flex justify-space-between align-center mb-1">
            <span class="text-caption">Progresso</span>
            <span class="text-caption font-weight-bold">
              {{ calculatedProgress }}%
            </span>
          </div>
          <v-progress-linear :model-value="calculatedProgress" :color="getProgressColor(calculatedProgress)" height="4"
            rounded bg-color="grey" />
        </div>

        <div v-if="hasSubtasks" class="subtasks-kanban mb-3">
          <div class="subtasks-header-kanban d-flex align-center justify-space-between pa-1 rounded cursor-pointer"
            @click.stop="toggleSubtasks" :class="{ 'subtasks-header-expanded': showSubtasks }">
            <div class="d-flex align-center">
              <v-icon size="14" class="mr-1" :class="{ 'rotate-90': showSubtasks }">
                mdi-chevron-right
              </v-icon>
              <span class="text-caption font-weight-medium">
                Subtarefas
              </span>
              <v-chip size="x-small" variant="flat" :color="getSubtasksProgressColor()" class="ml-1">
                {{ calculatedCompletedSubtasks }}/{{ calculatedTotalSubtasks }}
              </v-chip>
            </div>
          </div>

          <v-expand-transition>
            <div v-show="showSubtasks" class="mt-1">
              <div v-if="activity.children && activity.children.length > 0">
                <KanbanCard v-for="child in activity.children" :key="child.id" :activity="child"
                  @click="$emit('click', $event)" @edit="$emit('edit', $event)" @view="$emit('view', $event)"
                  @delete="$emit('delete', $event)" @addSubtask="$emit('addSubtask', $event)"
                  @editSubtask="$emit('editSubtask', $event)" @deleteSubtask="$emit('deleteSubtask', $event)"
                  @updateSubtaskStatus="$emit('updateSubtaskStatus', $event)"
                  @updateActivityStatus="$emit('updateActivityStatus', $event)"
                  @view-subtask-history="$emit('viewSubtaskHistory', $event)" class="ml-2 mb-1" />
              </div>

              <div v-else>
                <v-card v-for="subtask in activity.subtasks" :key="subtask.id" class="mb-1 pa-2" variant="outlined"
                  :class="getSubtaskCardClasses(subtask)">

                  <div class="d-flex align-start justify-space-between mb-2">
                    <div class="d-flex flex-grow-1 min-width-0">
                      <v-icon :color="getSubtaskIconColor(subtask.status)" size="14" class="mr-2 flex-shrink-0 mt-1">
                        {{ getSubtaskIcon(subtask.status) }}
                      </v-icon>
                      <span class="text-body-2 font-weight-medium text-wrap">
                        {{ subtask.order }}. {{ subtask.title }}
                      </span>

                      <v-chip v-if="subtask.isOverdue" color="error" size="x-small" variant="tonal"
                        class="ml-1 flex-shrink-0 mt-1">
                        Vencido
                      </v-chip>
                    </div>

                    <div class="mx-1 flex-shrink-0" style="width: fit-content;">
                      <v-select :model-value="subtask.status" :items="statusOptions" item-title="title"
                        item-value="value" density="compact" variant="flat" hide-details
                        :color="getStatusColor(subtask.status)"
                        @update:model-value="(newStatus) => handleSubtaskStatusChange(subtask.id, newStatus)"
                        @click.stop class="subtask-status-select" style="min-width: 100px; width: fit-content;">
                        <template #selection="{ item }">
                          <v-chip :color="getStatusColor(item.value)" size="x-small" variant="flat" class="px-2"
                            style="pointer-events: none;">
                            {{ item.order }}. {{ item.title }}
                          </v-chip>
                        </template>

                        <template #item="{ props, item }">
                          <v-list-item v-bind="props">
                            <template #prepend>
                              <v-icon :color="getStatusColor(item.value)" size="16">
                                {{ getStatusIcon(item.value) }}
                              </v-icon>
                            </template>
                          </v-list-item>
                        </template>
                      </v-select>
                    </div>

                    <v-menu v-if="authStore.isAdmin()">
                      <template #activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="x-small" @click.stop />
                      </template>
                      <v-list density="compact" min-width="160">
                        <v-list-item @click="handleViewSubtask(subtask.id)">
                          <template #prepend>
                            <v-icon size="16">mdi-eye-outline</v-icon>
                          </template>
                          <v-list-item-title>Visualizar</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="handleEditSubtask(subtask.id)">
                          <template #prepend>
                            <v-icon size="16">mdi-pencil</v-icon>
                          </template>
                          <v-list-item-title>Editar</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click="handleAddSubtask(subtask.id)">
                          <template #prepend>
                            <v-icon size="16">mdi-plus</v-icon>
                          </template>
                          <v-list-item-title>Criar Subtarefa</v-list-item-title>
                        </v-list-item>
                        <v-list-item v-if="authStore.isAdmin()" @click="handleViewSubtaskHistory(subtask.id)">
                          <template #prepend>
                            <v-icon size="16">mdi-history</v-icon>
                          </template>
                          <v-list-item-title>Histórico</v-list-item-title>
                        </v-list-item>
                        <v-divider />
                        <v-list-item @click="handleDeleteSubtask(subtask.id)" class="text-error">
                          <template #prepend>
                            <v-icon size="16" color="error">mdi-delete</v-icon>
                          </template>
                          <v-list-item-title>Excluir</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-menu v-else>
                      <template #activator="{ props }">
                        <v-btn v-bind="props" icon="mdi-dots-vertical" variant="text" size="x-small" @click.stop />
                      </template>
                      <v-list density="compact" min-width="160">
                        <v-list-item @click="handleViewSubtask(subtask.id)">
                          <template #prepend>
                            <v-icon size="16">mdi-eye-outline</v-icon>
                          </template>
                          <v-list-item-title>Visualizar</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                  </div>

                  <div class="d-flex flex-wrap gap-2 align-center mt-auto pt-2 border-t">
                    <div v-if="activity.dueDate" class="d-flex align-center">
                      <v-chip v-if="subtask.isOverdue" color="error" size="x-small" variant="tonal" class="me-2">
                        {{ formatDateCompact(subtask.dueDate) }}
                      </v-chip>

                      <v-chip v-else :color="getDateColor(subtask.dueDate)" size="x-small" variant="tonal" class="me-2">
                        <v-icon size="12" class="me-1">
                          mdi-calendar
                        </v-icon>
                        <span class="text-caption">
                          {{ formatDateCompact(subtask.dueDate) }}
                        </span>
                      </v-chip>

                      <v-chip size="x-small" variant="tonal" color="grey-darken-1">
                        <v-icon class="me-1">mdi-account-outline</v-icon>
                        {{ getSubtaskAssigneesText(subtask) }}
                      </v-chip>
                    </div>

                    <div class="d-flex align-center justify-end" style="flex: 1 1 0; max-width: 50%;">
                      <v-chip color="primary" size="small" variant="tonal">
                        <span class="text-caption">
                          {{ getProgressByStatus(subtask.statusId) }}<v-icon size="10">mdi-percent</v-icon>
                        </span>
                      </v-chip>
                    </div>
                  </div>
                </v-card>
              </div>

              <v-btn v-if="authStore.isAdmin()" variant="text" size="x-small" class="mt-1 w-100" prepend-icon="mdi-plus"
                @click.stop="handleAddSubtaskFromExpanded">
                <span class="text-caption">Adicionar</span>
              </v-btn>
            </div>
          </v-expand-transition>
        </div>

        <div v-if="activity.tags && activity.tags.length" class="mb-3">
          <v-chip v-for="tag in activity.tags.slice(0, 2)" :key="tag" size="x-small" variant="outlined" class="me-1">
            {{ tag }}
          </v-chip>
          <span v-if="activity.tags.length > 2" class="text-caption text-medium-emphasis">
            +{{ activity.tags.length - 2 }}
          </span>
        </div>

        <div class="d-flex flex-wrap gap-2 align-center">
          <div v-if="activity.dueDate" class="d-flex align-center">
            <v-chip v-if="activity.isOverdue" color="error" size="x-small" variant="tonal">
              {{ formatDateCompact(activity.dueDate) }}
            </v-chip>

            <v-chip v-else :color="getDateColor(activity.dueDate)" size="x-small" variant="tonal">
              <v-icon size="12" class="me-1">
                mdi-calendar
              </v-icon>
              <span class="text-caption">
                {{ formatDateCompact(activity.dueDate) }}
              </span>
            </v-chip>
          </div>

          <v-chip v-if="hasSubtasks && !showSubtasks" class="d-flex align-center ms-2" size="x-small" variant="tonal"
            color="primary">
            <v-icon size="12" class="me-1">
              mdi-format-list-checks
            </v-icon>
            <span class="text-caption">
              {{ calculatedCompletedSubtasks }}/{{ calculatedTotalSubtasks }} concluídas
            </span>
          </v-chip>

          <div v-if="hasAssignees" class="ms-2">
            <v-chip v-for="assignee in getAssigneeList.slice(0, 2)" :key="assignee.id || assignee" size="x-small"
              variant="tonal" color="grey-darken-1">
              <v-icon class="me-1">mdi-account-outline</v-icon>
              {{ assignee.name }}
            </v-chip>
            <span v-if="getAssigneeList.length > 2" class="text-caption text-medium-emphasis">
              +{{ getAssigneeList.length - 2 }}
            </span>
          </div>
        </div>
      </v-card-text>
    </v-card>

    <v-expand-transition>
      <div v-show="showChildren && hasChildren" class="children-container-kanban ml-2">
        <KanbanCard v-for="child in activity.children" :key="child.id" :activity="child" @click="$emit('click', $event)"
          @edit="$emit('edit', $event)" @view="$emit('view', $event)" @delete="$emit('delete', $event)"
          @addSubtask="$emit('addSubtask', $event)" @editSubtask="$emit('editSubtask', $event)"
          @deleteSubtask="$emit('deleteSubtask', $event)" @updateSubtaskStatus="$emit('updateSubtaskStatus', $event)"
          @updateActivityStatus="$emit('updateActivityStatus', $event)" />
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup>
import { ref, computed, inject } from 'vue'
import { useAuthStore } from '@/stores/auth'

import {
  canEditActivityUtil,
  canCreateSubtaskUtil,
  getAddSubtaskMessageUtil
} from '@/utils/permissions'

const authStore = useAuthStore()
const expansionState = inject('expansionState')

const props = defineProps({
  activity: {
    type: Object,
    required: true
  }
})

const emit = defineEmits([
  'click',
  'edit',
  'view',
  'viewHistory',
  'duplicate',
  'delete',
  'addSubtask',
  'editSubtask',
  'viewSubtaskHistory',
  'deleteSubtask',
  'updateSubtaskStatus',
  'updateActivityStatus'
])

const canEditActivity = computed(() => {
  return canEditActivityUtil(props.activity, authStore)
})

const canAddSubtask = computed(() => {
  return canCreateSubtaskUtil(props.activity, authStore)
})

const canEditOrAddSubtask = computed(() => {
  return canEditActivity.value || canAddSubtask.value
})

const getAddSubtaskMessage = (activity) => {
  return getAddSubtaskMessageUtil(activity, authStore)
}

const statusOptions = inject('statusOptions', computed(() => [
  { value: 'not_started', title: 'Não Iniciada', id: '1' },
  { value: 'in_progress', title: 'Em Andamento', id: '2' },
  { value: 'completed', title: 'Concluída', id: '3' }
]))

const showSubtasks = computed({
  get: () => expansionState.isSubtaskExpanded(props.activity.id),
  set: (value) => expansionState.setSubtaskExpanded(props.activity.id, value)
})

const showChildren = computed({
  get: () => expansionState.isActivityExpanded(props.activity.id),
  set: (value) => expansionState.setActivityExpanded(props.activity.id, value)
})

const hasChildren = computed(() => {
  return props.activity.children && props.activity.children.length > 0
})

const hasSubtasks = computed(() => {
  return (props.activity.subtasks && props.activity.subtasks.length > 0) || hasChildren.value
})

const calculatedProgress = computed(() => {
  if (props.activity.progress !== undefined) {
    return Math.round(props.activity.progress)
  }

  if (hasSubtasks.value) {
    const total = calculatedTotalSubtasks.value
    const completed = calculatedCompletedSubtasks.value
    if (total === 0) return 0
    return Math.round((completed / total) * 100)
  }

  const statusProgress = {
    'not_started': 0,
    'in_progress': 50,
    'completed': 100
  }
  return statusProgress[props.activity.status] || 0
})

const calculatedTotalSubtasks = computed(() => {
  if (hasChildren.value) {
    return props.activity.children.length
  }
  return props.activity.subtasks ? props.activity.subtasks.length : 0
})

const calculatedCompletedSubtasks = computed(() => {
  if (hasChildren.value) {
    return props.activity.children.filter(child => child.status === 'completed').length
  }
  return props.activity.subtasks ? props.activity.subtasks.filter(sub => sub.status === 'completed').length : 0
})

const toggleChildren = () => {
  expansionState.toggleActivityExpansion(props.activity.id)
}

const toggleSubtasks = () => {
  expansionState.toggleSubtaskExpansion(props.activity.id)
}

const hasAssignees = computed(() => {
  return (typeof props.activity.assignees === 'number' && props.activity.assignees > 0) ||
    (Array.isArray(props.activity.assignees) && props.activity.assignees.length > 0)
})

const getAssigneeList = computed(() => {
  if (Array.isArray(props.activity.assignees)) {
    if (props.activity.assignees.length > 0 && typeof props.activity.assignees[0] === 'string') {
      return props.activity.assignees.map((name, index) => ({
        id: index + 1,
        name: name,
        avatar: null
      }))
    }
    return props.activity.assignees
  }
  if (typeof props.activity.assignees === 'number') {
    return Array.from({ length: Math.min(props.activity.assignees, 5) }, (_, i) => ({
      id: i + 1,
      name: `Usuário ${i + 1}`,
      avatar: null
    }))
  }
  return []
})

const subtasksProgressPercentage = computed(() => {
  const total = calculatedTotalSubtasks.value
  const completed = calculatedCompletedSubtasks.value
  if (total === 0) return 0
  return Math.round((completed / total) * 100)
})

const getSubtaskAssignees = (subtask) => {
  if (Array.isArray(subtask.assignees)) {
    return subtask.assignees
  }
  if (Array.isArray(subtask.responsaveis)) {
    return subtask.responsaveis
  }
  return []
}

const getSubtaskAssigneesText = (subtask) => {
  const assignees = getSubtaskAssignees(subtask)
  if (assignees.length === 0) return 'Não atribuído'
  if (assignees.length === 1) {
    return typeof assignees[0] === 'string' ? assignees[0] : assignees[0].name || assignees[0]
  }
  return `${assignees.length} responsáveis`
}

const getSubtaskCardClasses = (subtask) => {
  const classes = []

  if (subtask.status === 'completed') {
    classes.push('subtask-completed')
  }

  if (subtask.isOverdue) {
    classes.push('subtask-overdue')
  }

  return classes
}

const handleCardClick = (event) => {
  if (!event.target.closest('.subtasks-kanban') && !event.target.closest('.children-container-kanban')) {

  }
}

const handleStatusChange = (newStatus) => {
  emit('updateActivityStatus', { activityId: props.activity.id, status: newStatus })
}

const handleEdit = () => {
  emit('edit', props.activity.id)
}

const handleViewHistory = () => {
  emit('viewHistory', props.activity.id)
}

const handleViewSubtaskHistory = (subtaskId) => {
  emit('viewSubtaskHistory', subtaskId)
}

const handleView = () => {
  emit('view', props.activity.id)
}

const handleAddSubtask = (parentId) => {
  const targetId = parentId || props.activity.id
  emit('addSubtask', targetId)
}

const handleAddSubtaskFromExpanded = () => {
  emit('addSubtask', props.activity.id)
}

const handleDelete = () => {
  emit('delete', props.activity.id)
}

const handleEditSubtask = (subtaskId) => {
  emit('editSubtask', subtaskId)
}

const handleViewSubtask = (subtaskId) => {
  let subtask = props.activity.subtasks?.find(s => s.id === subtaskId);

  if (!subtask && props.activity.children) {
    subtask = props.activity.children.find(child => child.id === subtaskId);
  }

  if (subtask) {

    emit('view', subtask);
  } else {
    console.warn('Subtarefa não encontrada:', subtaskId);
  }
}

const handleDeleteSubtask = (subtaskId) => {
  emit('deleteSubtask', subtaskId)
}

const handleSubtaskStatusChange = (subtaskId, newStatus) => {
  emit('updateSubtaskStatus', { subtaskId, status: newStatus })
}

const getProgressColor = (progress) => {
  if (progress >= 100) return 'success'
  if (progress >= 75) return 'info'
  if (progress >= 50) return 'warning'
  if (progress >= 25) return 'orange'
  return 'error'
}

const getPriorityColor = (priority) => {
  const colors = {
    'low': 'success',
    'medium': 'warning',
    'high': 'error'
  }
  return colors[priority] || 'grey'
}

const getPriorityText = (priority) => {
  const texts = {
    'low': 'Baixa',
    'medium': 'Média',
    'high': 'Alta'
  }
  return texts[priority] || priority
}

const getStatusColor = (status) => {
  const colors = {
    'not_started': 'grey',
    'in_progress': 'warning',
    'completed': 'success',
    'overdue': 'error'
  }
  return colors[status] || 'grey'
}

const getStatusText = (status) => {
  const texts = {
    'not_started': 'Não Iniciada',
    'in_progress': 'Em Andamento',
    'completed': 'Concluída',
    'overdue': 'Atrasada'
  }
  return texts[status] || status
}

const getStatusIcon = (status) => {
  const icons = {
    'not_started': 'mdi-circle-outline',
    'in_progress': 'mdi-clock-outline',
    'completed': 'mdi-check-circle',
    'overdue': 'mdi-alert-circle'
  }
  return icons[status] || 'mdi-circle-outline'
}

const getSubtaskIcon = (status) => {
  const icons = {
    'not_started': 'mdi-circle-outline',
    'in_progress': 'mdi-clock-outline',
    'completed': 'mdi-check-circle',
    'overdue': 'mdi-alert-circle'
  }
  return icons[status] || 'mdi-circle-outline'
}

const getSubtaskIconColor = (status) => {
  const colors = {
    'not_started': 'grey',
    'in_progress': 'warning',
    'completed': 'success',
    'overdue': 'error'
  }
  return colors[status] || 'grey'
}

const getProgressByStatus = (status) => {
  switch (status) {
    case '3':
      return '100'
    case '2':
      return '50'
    default:
      return '0'
  }
}

const getSubtasksProgressColor = () => {
  const percentage = subtasksProgressPercentage.value
  if (percentage === 100) return 'success'
  if (percentage >= 50) return 'warning'
  if (percentage > 0) return 'info'
  return 'grey'
}

const formatDateCompact = (dateString) => {
  if (!dateString) return ''

  const [year, month, day] = dateString.split('-').map(Number)
  const date = new Date(year, month - 1, day)

  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const diffTime = date.getTime() - today.getTime()
  const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return 'Vence hoje'
  if (diffDays === 1) return 'Vence amanhã'
  if (diffDays === -1) return 'Venceu ontem'
  if (diffDays > 0 && diffDays <= 7) return `Em ${diffDays} dias`
  if (diffDays < 0 && diffDays >= -7) return `Vencido há ${Math.abs(diffDays)} dias`

  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
}

const getDateClass = (dueDate) => {
  if (!dueDate) return 'text-medium-emphasis'

  const today = new Date()
  const due = new Date(dueDate)

  if (due < today) return 'text-error font-weight-bold'
  if (due.getTime() - today.getTime() < 7 * 86400000) return 'text-warning font-weight-medium'
  return 'text-medium-emphasis'
}

const getDateColor = (dueDate) => {
  if (!dueDate) return 'medium-emphasis'

  const today = new Date()
  const due = new Date(dueDate)
  if (due < today) return 'error'
  if (due.getTime() - today.getTime() < 7 * 86400000) return 'warning'
  return 'medium-emphasis'
}

const getInitials = (name) => {
  if (!name) return '?'
  return name
}
</script>

<style scoped>
.kanban-hierarchy-wrapper {
  position: relative;
}

.hierarchy-indicator-kanban {
  position: absolute;
  left: -12px;
  top: 16px;
  display: flex;
  align-items: center;
}

.children-container-kanban {
  position: relative;
  border-left: 1px solid rgb(var(--v-theme-outline-variant));
  padding-left: 8px;
}

.children-container-kanban::before {
  content: '';
  position: absolute;
  left: -1px;
  top: 0;
  bottom: 0;
  width: 1px;
  background-color: rgb(var(--v-theme-outline-variant));
}

/* Estilos originais mantidos */
.border-l-4 {
  border-left: 4px solid;
}

.border-not_started {
  border-left-color: rgb(var(--v-theme-surface-variant));
}

.border-in_progress {
  border-left-color: rgb(var(--v-theme-warning));
}

.border-completed {
  border-left-color: rgb(var(--v-theme-success));
}

.border-overdue {
  border-left-color: rgb(var(--v-theme-error));
}

.has-alert {
  box-shadow: 0 0 0 1px rgba(var(--v-theme-warning), 0.3);
}

.description-text {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.3;
}

/* Estilos para seção de subtarefas no Kanban (compacta) */
.subtasks-kanban {
  border: 1px solid rgb(var(--v-theme-outline-variant));
  background-color: rgb(var(--v-theme-surface-container));
  border-radius: 6px;
  overflow: hidden;
}

.subtasks-header-kanban {
  transition: all 0.2s ease;
  user-select: none;
  background-color: rgb(var(--v-theme-surface-container-low));
  border-bottom: 1px solid rgb(var(--v-theme-outline-variant));
}

.subtasks-header-kanban:hover {
  background-color: rgb(var(--v-theme-surface-container));
}

.subtasks-header-expanded {
  background-color: rgb(var(--v-theme-surface-container));
}

.subtask-card {
  transition: all 0.2s ease;
  background-color: rgb(var(--v-theme-surface-bright));
}

.subtask-card:hover {
  background-color: rgb(var(--v-theme-surface-container-high));
}

.subtask-completed {
  opacity: 0.8;
  background-color: rgba(var(--v-theme-success), 0.05);
}

.subtask-overdue {
  background-color: rgba(var(--v-theme-error), 0.05);
  border-color: rgba(var(--v-theme-error), 0.3);
}

/* Animação do ícone de expansão */
.rotate-90 {
  transform: rotate(90deg);
  transition: transform 0.2s ease;
}

/* Cursor pointer para header */
.cursor-pointer {
  cursor: pointer;
}

/* Estilos para o select de status das subtarefas */
.subtask-status-select :deep(.v-field) {
  min-height: 28px;
}

.subtask-status-select :deep(.v-field__input) {
  min-height: 24px;
  padding: 2px 6px;
}

/* Estilo compacto para Kanban */
.kanban-card {
  max-width: 100%;
  min-height: auto;
}

.kanban-card .v-card-title {
  padding: 8px 12px 4px 12px;
}

.kanban-card .v-card-text {
  padding: 4px 12px 8px 12px;
}

@media (max-width: 600px) {
  .kanban-card {
    margin-bottom: 8px;
  }

  .v-card-title {
    padding: 8px !important;
  }

  .v-card-text {
    padding: 0 8px 8px !important;
  }

  .hierarchy-indicator-kanban {
    left: -8px;
  }

  .children-container-kanban {
    padding-left: 4px;
  }

  .subtask-status-select {
    width: 80px !important;
  }
}
</style>
